import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { LoadingPanel } from "../../components/layout/Loading";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import { MdDelete } from "react-icons/md";
import {
  ActiveInactiveTermsAndConditions,
  deleteTermsAndConditions,
  getAllTermsAndConditions,
} from "./services/termsAndCondition.services";
import {
  clearTermsAndConditionDetails,
  clearTermsAndConditionID,
  setTermsAndConditionID,
} from "./termsAndConditionSlice";
import CreateTermsAndCondition from "./CreateTermsAndCondition";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import ButtonCreate from "../../components/muidrawer/ButtonCreate";
import AppDrawer from "../../components/muidrawer/Drawer";
import ButtonEdit from "../../components/muidrawer/ButtonEdit";

const TermsAndCondition: React.FC = () => {
  const dispatch = useAppDispatch();
  const isDrawerOpen = useAppSelector((state) => state.muidrawer.open);
  const drawerName = useAppSelector((state) => state.muidrawer.name);
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllTermsAndConditions());
    return () => {
      dispatch(clearTermsAndConditionDetails());
      dispatch(closeDialog());
      dispatch(closed());
    };
  }, []);

  return (
    <>
      <DeleteTAndCDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Terms And Conditions List
          </Typography.h4>
          {isAddAccess && (
            <ButtonCreate label="Add New" drawerName="termsAndConditionForm" />
          )}
        </GridLayoutItem>
        <GridLayoutItem>
          <TermsAndConditionGridComponent />
        </GridLayoutItem>
        {isDrawerOpen && drawerName === "termsAndConditionForm" && (
          <AppDrawer>
            <CreateTermsAndCondition />
          </AppDrawer>
        )}
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 50 };

const TermsAndConditionGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const TermsAndConditionList = useAppSelector(
    (state) => state.termsandcondition.TermsAndConditionList
  );
  const loading = useAppSelector((state) => state.termsandcondition.loading);

  const [expandedRow, setExpandedRow] = useState(null);
  const truncateRemarks = (remarks: string) => {
    if (!remarks) return "";
    const maxLength = 100;
    if (remarks.length <= maxLength) return remarks;
    return remarks.substring(0, maxLength) + "...";
  };

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All"
        ? TermsAndConditionList.length
        : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleOpenDeleteDialog = (terms_and_condition_guid: string) => {
      dispatch(setTermsAndConditionID(terms_and_condition_guid));
      dispatch(openDialog("deleteTermsAndCondition"));
    };

    return (
      <td
        className="action-td"
        style={{
          ...props.style,
        }}
      >
        {isEditAccess && (
          <ButtonEdit
            ID={props.dataItem.terms_and_condition_guid}
            name="termsAndConditionForm"
            tooltipTitle="Edit"
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() =>
              handleOpenDeleteDialog(props.dataItem.terms_and_condition_guid)
            }
          >
            <MdDelete
              className="absolute-position"
              style={{ fontSize: "24px" }}
            />
          </IconButton>
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const MyActiveInActtiveCommandCell = (props: GridCellProps) => {
    const [IsActive, setIsActive] = useState<boolean>(props.dataItem.isactive);

    const handleActiveInActive = () => {
      setIsActive(!IsActive);
      const data = {
        terms_and_condition_guid: props.dataItem.terms_and_condition_guid,
        isactive: !IsActive,
      };
      dispatch(ActiveInactiveTermsAndConditions(data)).then((response: any) => {
        if (response.payload?.data?.Status === 200) {
          dispatch(getAllTermsAndConditions());
        } else {
          dispatch(getAllTermsAndConditions());
        }
      });
    };

    return (
      <td style={{ textAlign: "center" }}>
        <Switch
          disabled={isEditAccess ? false : true}
          name="isactive"
          checked={IsActive}
          onChange={handleActiveInActive}
          size="small"
        />
      </td>
    );
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {TermsAndConditionList && TermsAndConditionList.length ? (
        <KendoGrid
         style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={
            filter
              ? filterBy(TermsAndConditionList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : TermsAndConditionList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={TermsAndConditionList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [10, 25, 50, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="terms_and_condition"
            title="Terms And Conditions"
            cell={(props: any) => (
              <td
                className={`${isEditAccess ? "hoverableName" : "normalName"}`}
              >
                {isEditAccess ? (
                  <ButtonEdit
                    ID={props.dataItem.terms_and_condition_guid}
                    name="termsAndConditionForm"
                    label={props.dataItem?.terms_and_condition}
                  />
                ) : (
                  props.dataItem?.terms_and_condition
                )}
              </td>
            )}
          />
          <Column
            field="description"
            title="Description"
            cell={(props: any) => (
              <td>
                <div
                  className="remarks-content"
                  dangerouslySetInnerHTML={{
                    __html:
                      expandedRow === props.dataItem?.id
                        ? props.dataItem?.description
                        : truncateRemarks(props.dataItem?.description),
                  }}
                />
                {props.dataItem?.description.length > 100 && (
                  <span
                    className="readmore-btn"
                    onClick={() =>
                      setExpandedRow(
                        expandedRow === props.dataItem?.id
                          ? null
                          : props.dataItem?.id
                      )
                    }
                  >
                    {expandedRow === props.dataItem?.id
                      ? "Read Less"
                      : "Read More"}
                  </span>
                )}
              </td>
            )}
          />
          <Column
            field="id"
            title="Status"
            cell={MyActiveInActtiveCommandCell}
            width={"90px"}
            filterable={false}
            locked={true}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="id"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteTAndCDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const TermsAndConditionID = useAppSelector(
    (state) => state.termsandcondition.TermsAndConditionID
  );

  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = async (terms_and_condition_guid: any) => {
    if (terms_and_condition_guid) {
      const response = await dispatch(
        deleteTermsAndConditions(terms_and_condition_guid)
      );
      if (response.payload.Data.Status === 200) {
        dispatch(getAllTermsAndConditions());
        dispatch(closeDialog());
        dispatch(clearTermsAndConditionID());
      } else {
        dispatch(getAllTermsAndConditions());
        dispatch(closeDialog());
        dispatch(clearTermsAndConditionID());
      }
    }
  };
  return (
    <>
      {dialogName === "deleteTermsAndCondition" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Terms & Condition"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Terms & Condition?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(TermsAndConditionID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};
export default TermsAndCondition;
